import { Link, useLocation, Navigate } from "react-router-dom";
import { CheckCircle2, Package, Store, MapPin, Clock } from "lucide-react";
import { formatPrice } from "@/lib/api";
import { useStoreInfo } from "@/context/StoreInfoContext";

export default function OrderSuccess() {
  const { state } = useLocation();
  const store = useStoreInfo();
  const order = state?.order;
  if (!order) return <Navigate to="/" replace />;

  const isPickup = order.fulfillment_type === "pickup";

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6 lg:px-8" data-testid="order-success-page">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-brand-teal/15 text-brand-teal">
        <CheckCircle2 size={40} />
      </div>
      <h1 className="mt-6 font-display text-4xl font-bold text-brand-navy">
        {isPickup ? "Order confirmed — we'll prep it!" : "Order confirmed!"}
      </h1>
      <p className="mt-3 text-slate-600">
        {isPickup
          ? "Thanks! We'll email you the moment your order is ready for pickup — usually within 2 hours during store hours."
          : "Thanks for supporting your local game store. A receipt has been emailed to you from Square with all the details."}
      </p>

      {isPickup && (
        <div className="mt-8 rounded-2xl border-2 border-brand-teal/40 bg-brand-teal/5 p-6 text-left" data-testid="pickup-info-card">
          <div className="flex items-center gap-2 text-brand-teal">
            <Store size={16} />
            <span className="text-xs font-bold uppercase tracking-widest">Pick up at</span>
          </div>
          <p className="mt-2 font-display text-xl font-bold text-brand-navy">{store.name}</p>
          {store.address_full && (
            <p className="mt-1 flex items-center gap-2 text-sm text-slate-700">
              <MapPin size={14} className="text-brand-teal" /> {store.address_full}
            </p>
          )}
          {store.hours && (
            <p className="mt-1 flex items-center gap-2 text-sm text-slate-700">
              <Clock size={14} className="text-brand-teal" /> {store.hours}
            </p>
          )}
          <p className="mt-4 text-xs text-slate-500">
            Bring a photo ID and mention order <span className="font-mono font-bold">{order.order_id}</span> at the counter.
          </p>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 text-left">
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Order ID</p>
            <p className="font-mono text-lg font-bold text-brand-navy" data-testid="order-id">
              {order.order_id}
            </p>
          </div>
          <span className="chip bg-emerald-50 text-emerald-700">Paid</span>
        </div>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-600">Items</dt>
            <dd className="font-semibold text-brand-navy">{order.items_count}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-600">Fulfillment</dt>
            <dd className="font-semibold text-brand-navy">
              {isPickup ? "In-store pickup" : "Standard shipping"}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-600">Total charged</dt>
            <dd className="font-display text-lg font-extrabold text-brand-navy" data-testid="order-total">
              {formatPrice(order.total)}
            </dd>
          </div>
        </dl>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/shop"
          className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide"
        >
          <Package size={16} /> Keep shopping
        </Link>
        <Link
          to="/events"
          className="inline-flex items-center gap-2 rounded-full border-2 border-brand-navy px-6 py-3 text-sm font-bold uppercase tracking-wide text-brand-navy hover:bg-brand-navy hover:text-white"
        >
          See upcoming events
        </Link>
      </div>
    </div>
  );
}
